import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api } from '../../services/api';
import {
  RefreshCw, Loader2, Brain, Plus, Trash2, Save,
  X, ToggleLeft, ToggleRight, Play, Upload, AlertTriangle, AlertCircle, DollarSign, Info,
  FileText, Copy, Check, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Settings2,
} from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import {
  validatePromptPayload, formatTrafficPct, temperaturePolicyHint,
  PROMPT_TYPES, PROMPT_STATES,
} from '../../utils/promptValidation';

// ─── Prompt Name Mapping ────────────────────────────────
const FUNCTION_TO_PROMPT: Record<string, string> = {
  'gen_summary_ai_generate': 'gen_summary',
  'gen_skills_embedding': 'gen_skills',
  'gen_skills_ai_generate': 'gen_skills_generate',
  'gen_highlights_ai_generate': 'gen_highlights',
  'gen_experience_embedding': 'gen_experience',
  'gen_experience_ai_generate': 'gen_experience_generate',
  'gen_education_embedding': 'gen_education',
  'gen_awards_embedding': 'gen_awards',
  'gen_presentations_embedding': 'gen_presentations',
  'gen_publications_embedding': 'gen_publications',
  'gen_portfolios_embedding': 'gen_portfolios',
  'gen_coverletter_ai_generate': 'gen_coverletter',
  'gen_elevatorpitch_ai_generate': 'gen_elevatorpitch',
  'gen_summary_phrase_embedding': 'gen_summary_phrases',
  'gen_tags_resume': 'gen_tags_resume',
  'gen_tags_resume_items': 'gen_tags_resume_items',
  'gen_tags_jobdesc': 'gen_tags_jobdesc',
  // Per-item (atomic) resume tag generators — R5. function_id == prompt_name.
  'gen_skill_tags': 'gen_skill_tags',
  'gen_bullet_tags': 'gen_bullet_tags',
  'gen_education_tags': 'gen_education_tags',
  'gen_award_tags': 'gen_award_tags',
  'gen_presentation_tags': 'gen_presentation_tags',
  'gen_publication_tags': 'gen_publication_tags',
  'gen_portfolio_tags': 'gen_portfolio_tags',
  'import_pdf_to_resume': 'import_pdf_resume',
  'import_txt_to_resume': 'import_txt_resume',
  'import_validate_resume': 'import_validate_resume',
  'validate_resume': 'validate_resume',
  'validate_jobdesc': 'validate_jobdesc',
  'review_resume': 'review_resume',
  'fix_yaml': 'fix_yaml',
  'error_monitor_classify': 'error_monitor_classify',
  'validate_experience': 'validate_experience',
  'validate_summary': 'validate_summary',
  'validate_highlights': 'validate_highlights',
  'validate_coverletter': 'validate_coverletter',
  'fix_experience': 'fix_experience',
  'fix_summary': 'fix_summary',
  'fix_highlights': 'fix_highlights',
  'fix_coverletter': 'fix_coverletter',
  'qa_feedback': 'qa_feedback',
};

// ─── Common Prompt Parameters Reference ─────────────────
const PROMPT_PARAMETERS: { name: string; description: string }[] = [
  { name: '{{CONTACT_ICON_LIST}}', description: 'Valid contact icon identifiers (from global settings)' },
  { name: '{{CUTOFF_YEAR}}', description: 'User-configured cutoff year for experience filtering' },
  { name: '{{DISMISSED_RECOMMENDATIONS}}', description: 'Previously dismissed AI recommendations (JSON array, or "None")' },
  { name: '{{ERROR_LOGS}}', description: 'CloudWatch error log entries for classification (error monitor)' },
  { name: '{{JOB_COMPANY}}', description: 'Company name only (e.g., "Acme Inc.")' },
  { name: '{{JOB_DESC}}', description: 'Full job description text' },
  { name: '{{JOB_TAGS}}', description: 'Pre-computed job description tags (comma-separated requirements, skills)' },
  { name: '{{JOB_TITLE}}', description: 'Job title only (e.g., "Senior Software Engineer")' },
  { name: '{{NONCE}}', description: 'Random 12-char hex nonce for generation variety (use via {{GET_UNIQUE_RESPONSE}} snippet)' },
  { name: '{{OPEN_TICKETS}}', description: 'Existing open work queue items for deduplication (error monitor)' },
  { name: '{{RESUME_FULL_JSON}}', description: 'Full resume JSON with all sections and tags' },
  { name: '{{RESUME}}', description: 'Alias for RESUME_FULL_JSON (backward compat)' },
  { name: '{{RESUME_DATA}}', description: 'Full resume JSON data (used by tag generators)' },
  { name: '{{RESUME_INPUT}}', description: 'Raw text/PDF content for resume import parsing' },
  { name: '{{USERJOB_CONTACT_YAML}}', description: 'Contact section scored YAML (999 prefix, no tags)' },
  { name: '{{USERJOB_SKILLS_YAML}}', description: 'Skills section scored YAML (3-char prefix, no tags)' },
  { name: '{{USERJOB_EXPERIENCE_YAML}}', description: 'Experience section scored YAML (3-char prefix on bullets, no tags)' },
  { name: '{{USERJOB_EDUCATION_YAML}}', description: 'Education section scored YAML (3-char prefix, no tags)' },
  { name: '{{USERJOB_AWARDS_YAML}}', description: 'Awards section scored YAML (3-char prefix, no tags)' },
  { name: '{{USERJOB_PRESENTATIONS_YAML}}', description: 'Presentations section scored YAML (3-char prefix, no tags)' },
  { name: '{{USERJOB_PUBLICATIONS_YAML}}', description: 'Publications section scored YAML (3-char prefix, no tags)' },
  { name: '{{USERJOB_PORTFOLIOS_YAML}}', description: 'Portfolios section scored YAML (3-char prefix, no tags)' },
  { name: '{{RESUME_SUMMARY_PHRASES}}', description: 'Scored summary phrases from resume (embedding-matched, 000 filtered out). Each phrase prefixed with 3-char match score.' },
  { name: '{{RESUME_TAGS}}', description: 'Pre-computed resume tags (comma-separated skills, domains, themes)' },
  { name: '{{RESUME_TEMPLATE}}', description: 'Full resume schema template (all sections) for format guidance' },
  { name: '{{RESUME_TEMPLATE.AWARDS}}', description: 'Awards section schema template' },
  { name: '{{RESUME_TEMPLATE.CONTACT}}', description: 'Contact section schema template' },
  { name: '{{RESUME_TEMPLATE.EDUCATION}}', description: 'Education section schema template' },
  { name: '{{RESUME_TEMPLATE.EXPERIENCE}}', description: 'Experience section schema template (companies, roles, bullets)' },
  { name: '{{RESUME_TEMPLATE.HIGHLIGHTS}}', description: 'Highlights section schema template' },
  { name: '{{RESUME_TEMPLATE.PORTFOLIOS}}', description: 'Portfolios section schema template' },
  { name: '{{RESUME_TEMPLATE.PRESENTATIONS}}', description: 'Presentations section schema template' },
  { name: '{{RESUME_TEMPLATE.PUBLICATIONS}}', description: 'Publications section schema template' },
  { name: '{{RESUME_TEMPLATE.SKILLS}}', description: 'Skills section schema template' },
  { name: '{{RESUME_TEMPLATE.SUMMARY}}', description: 'Summary section schema template' },
  { name: '{{VALIDATION_INPUT}}', description: 'AI-generated content being validated or fixed (YAML)' },
  { name: '{{VALIDATION_ISSUES}}', description: 'List of validation errors or fabrication issues found (JSON)' },
  { name: '{{YAML_CONTENT}}', description: 'The YAML content with syntax errors (fix_yaml)' },
  { name: '{{YAML_ERROR}}', description: 'YAML parse error message (fix_yaml)' },
];

// Types
interface AIModel {
  model_id: string;
  display_name: string;
  enabled: boolean;
  model_type?: 'llm' | 'embedding';
  input_cost_per_1k?: number | null;
  output_cost_per_1k?: number | null;
}

interface PostProcessorParam {
  type: string;
  required: boolean;
  default?: unknown;
  description: string;
}

interface PostProcessorMeta {
  name: string;
  description: string;
  params: Record<string, PostProcessorParam>;
}

interface PostProcessorConfig {
  fn: string;
  params: Record<string, unknown>;
}

interface ModelAssignment {
  function_id: string;
  model_id: string;
  description: string;
  ai_pct?: number;
  token_in_formula?: string;
  token_out_formula?: string;
  max_tokens?: number | null;
  post_processors?: PostProcessorConfig[];
}

interface CostConfig {
  job_desc: number;
  resume: number;
  small_section: number;
  experience_section: number;
  resume_tags: number;
  job_tags: number;
  jobs_per_month: number;
}

interface ModelPricingEntry {
  model_id: string;
  input_cost_per_1k: number | null;
  output_cost_per_1k: number | null;
}

const DEFAULT_COST_CONFIG: CostConfig = {
  job_desc: 1500,
  resume: 3000,
  small_section: 120,
  experience_section: 1000,
  resume_tags: 400,
  job_tags: 400,
  jobs_per_month: 100,
};

// ─── Helper: resolve variable formula to token count ────────
function resolveFormula(formula: string | undefined, vars: CostConfig): number {
  if (!formula) return 0;
  // Replace {variable} references with their values, then sum the parts
  let resolved = formula;
  resolved = resolved.replace(/\{job_desc\}/g, String(vars.job_desc));
  resolved = resolved.replace(/\{resume\}/g, String(vars.resume));
  resolved = resolved.replace(/\{small_section\}/g, String(vars.small_section));
  resolved = resolved.replace(/\{experience_section\}/g, String(vars.experience_section));
  resolved = resolved.replace(/\{resume_tags\}/g, String(vars.resume_tags));
  resolved = resolved.replace(/\{job_tags\}/g, String(vars.job_tags));
  // Evaluate simple addition: "1500 + 120" → 1620
  try {
    const parts = resolved.split('+').map((p) => parseFloat(p.trim())).filter((n) => !isNaN(n));
    return parts.reduce((sum, n) => sum + n, 0);
  } catch {
    return 0;
  }
}

// ─── Helper: calculate cost per request for a function ──────
function calcCostPerRequest(
  assignment: ModelAssignment,
  costConfig: CostConfig,
  pricing: Record<string, ModelPricingEntry>,
): number {
  const aiPct = (assignment.ai_pct ?? 0) / 100;
  const tokenIn = resolveFormula(assignment.token_in_formula, costConfig);
  const tokenOut = resolveFormula(assignment.token_out_formula, costConfig);
  const modelPricing = pricing[assignment.model_id];
  if (!modelPricing) return 0;
  const inCost = modelPricing.input_cost_per_1k ?? 0;
  const outCost = modelPricing.output_cost_per_1k ?? 0;
  return (tokenIn / 1000) * inCost * aiPct + (tokenOut / 1000) * outCost * aiPct;
}

// ─── Main Model Management Page ──────────────────────────
export function ModelManagement() {
  const [models, setModels] = useState<AIModel[]>([]);
  const [assignments, setAssignments] = useState<ModelAssignment[]>([]);
  const [costConfig, setCostConfig] = useState<CostConfig>(DEFAULT_COST_CONFIG);
  const [pricing, setPricing] = useState<Record<string, ModelPricingEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [cacheCleared, setCacheCleared] = useState(false);

  // Add model form state
  const [searchQuery, setSearchQuery] = useState('');
  const [addModelType, setAddModelType] = useState<'llm' | 'embedding'>('llm');
  const [addingModel, setAddingModel] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<Array<{ model_id: string; model_name: string; provider: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }>>([]);
  const [searching, setSearching] = useState(false);

  // Edit model state
  const [editingModel, setEditingModel] = useState<string | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');

  // Test model state
  const [testingModel, setTestingModel] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ modelId: string; response: string } | null>(null);

  // Assignment save state
  const [savingAssignment, setSavingAssignment] = useState<string | null>(null);
  // Cost config save state
  const [savingCostConfig, setSavingCostConfig] = useState(false);
  // Inline edit state for function cost fields
  const [editingField, setEditingField] = useState<{ functionId: string; field: string } | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  // Refresh pricing state
  const [refreshingPricing, setRefreshingPricing] = useState(false);
  const [pricingDebug, setPricingDebug] = useState<{
    unmatched_models?: Array<{ model_id: string; display_name: string }>;
    available_pricing_names?: string[];
  } | null>(null);

  // ─── Prompt Editor State ─────────────────────────────────
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [selectedPromptName, setSelectedPromptName] = useState('');
  const [promptVersions, setPromptVersions] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);
  const [flashNewVersion, setFlashNewVersion] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<any>(null);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);
  // Non-blocking governance notes (e.g. a runtime {{TOKEN}} in the cached static block)
  const [promptWarnings, setPromptWarnings] = useState<string[]>([]);
  const [autoIncrementPatch, setAutoIncrementPatch] = useState(() =>
    localStorage.getItem('admin_auto_increment_patch') !== 'false'
  );
  const [promptFilter, setPromptFilter] = useState({ type: '', state: '' });
  const [versionPanelOpen, setVersionPanelOpen] = useState(() => window.innerWidth >= 768);

  // ─── Lightbox Function Config State ────────────────────
  const [lightboxFunctionId, setLightboxFunctionId] = useState<string>('');
  const [lightboxDescription, setLightboxDescription] = useState<string>('');
  const [lightboxModelId, setLightboxModelId] = useState<string>('');
  const [lightboxMaxTokens, setLightboxMaxTokens] = useState<string>('');

  // ─── Post-Processor State ──────────────────────────────
  const [postProcessorRegistry, setPostProcessorRegistry] = useState<PostProcessorMeta[]>([]);
  const [lightboxPostProcessors, setLightboxPostProcessors] = useState<PostProcessorConfig[]>([]);

  // ─── Snippets State ───────────────────────────────────
  const [snippets, setSnippets] = useState<{ prompt_name: string; prompt_version: string; prompt_state: string }[]>([]);

  // ─── Snippet Management State ──────────────────────────
  const [addingSnippet, setAddingSnippet] = useState(false);
  const [newSnippetName, setNewSnippetName] = useState('');
  const [deletingSnippet, setDeletingSnippet] = useState<string | null>(null);

  // ─── Clipboard Copy State ─────────────────────────────
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 1500);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const bundle = await api.getAdminAiConfigBundle();

      const loadedModels = (bundle.models || []) as AIModel[];
      setModels(loadedModels);

      // Separate _cost_config from function assignments
      const allItems = bundle.functions || [];
      const configItem = allItems.find((f) => f.function_id === '_cost_config');
      const funcItems = allItems.filter((f) => f.function_id !== '_cost_config');
      setAssignments(funcItems as ModelAssignment[]);

      if (configItem) {
        setCostConfig({
          job_desc: Number(configItem.job_desc ?? DEFAULT_COST_CONFIG.job_desc),
          resume: Number(configItem.resume ?? DEFAULT_COST_CONFIG.resume),
          small_section: Number(configItem.small_section ?? DEFAULT_COST_CONFIG.small_section),
          experience_section: Number(configItem.experience_section ?? DEFAULT_COST_CONFIG.experience_section),
          resume_tags: Number(configItem.resume_tags ?? DEFAULT_COST_CONFIG.resume_tags),
          job_tags: Number(configItem.job_tags ?? DEFAULT_COST_CONFIG.job_tags),
          jobs_per_month: Number(configItem.jobs_per_month ?? DEFAULT_COST_CONFIG.jobs_per_month),
        });
      }

      // Merge bundle pricing with stored model pricing (stored takes precedence)
      const apiPricing = bundle.pricing || {};
      const merged: Record<string, ModelPricingEntry> = { ...apiPricing };
      for (const m of loadedModels) {
        if (m.input_cost_per_1k != null || m.output_cost_per_1k != null) {
          merged[m.model_id] = {
            model_id: m.model_id,
            input_cost_per_1k: m.input_cost_per_1k ?? apiPricing[m.model_id]?.input_cost_per_1k ?? null,
            output_cost_per_1k: m.output_cost_per_1k ?? apiPricing[m.model_id]?.output_cost_per_1k ?? null,
          };
        }
      }
      setPricing(merged);

      setSnippets((bundle.snippets || []).slice().sort((a, b) => a.prompt_name.localeCompare(b.prompt_name)));
      setPostProcessorRegistry(bundle.post_processors || []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to load model data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useAutoRefresh(loadData);

  // ─── Model Registry Handlers ────────────────────────────
  const handleSearchModels = async () => {
    const query = searchQuery.trim();
    if (!query || query.length < 2) return;
    setSearching(true);
    setError(null);
    setSearchResults([]);
    try {
      const result = await api.searchBedrockModels(query);
      // Filter out models already in the registry
      const existingIds = new Set(models.map((m) => m.model_id));
      const filtered = (result.matches || []).filter((m) => !existingIds.has(m.model_id));
      setSearchResults(filtered);
      if (filtered.length === 0 && (result.matches || []).length > 0) {
        setError(`All ${result.matches.length} matching models are already in your registry`);
      } else if (filtered.length === 0) {
        setError(`No Bedrock models found matching "${query}"`);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to search models');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectAndAddModel = async (match: { model_id: string; model_name: string; input_cost_per_1k: number | null; output_cost_per_1k: number | null }) => {
    setAddingModel(match.model_id);
    setError(null);
    try {
      await api.updateModel(match.model_id, {
        display_name: match.model_name,
        enabled: true,
        model_type: addModelType,
        input_cost_per_1k: match.input_cost_per_1k,
        output_cost_per_1k: match.output_cost_per_1k,
      });
      setSearchQuery('');
      setSearchResults([]);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to add model');
    } finally {
      setAddingModel(null);
    }
  };

  const handleToggleEnabled = async (model: AIModel) => {
    setError(null);
    try {
      await api.updateModel(model.model_id, {
        display_name: model.display_name,
        enabled: !model.enabled,
      });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to toggle model');
    }
  };

  const handleEditSave = async (model: AIModel) => {
    if (!editDisplayName.trim()) return;
    setError(null);
    try {
      await api.updateModel(model.model_id, {
        display_name: editDisplayName.trim(),
        enabled: model.enabled,
      });
      setEditingModel(null);
      setEditDisplayName('');
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update model');
    }
  };

  const handleDeleteModel = async (modelId: string) => {
    if (!confirm(`Delete model "${modelId}"? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteModel(modelId);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to delete model');
    }
  };

  const handleTestModel = async (modelId: string) => {
    setTestingModel(modelId);
    setTestResult(null);
    setError(null);
    try {
      const model = models.find(m => m.model_id === modelId);
      const testType = model?.model_type === 'embedding' ? 'embedding' : undefined;
      const result = await api.testModel(modelId, testType);
      setTestResult({ modelId, response: result.response });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to test model');
    } finally {
      setTestingModel(null);
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will seed the default AI models into the system. Continue?')) return;
    setSeeding(true);
    setError(null);
    try {
      await api.seedModels();
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to seed models');
    } finally {
      setSeeding(false);
    }
  };

  const handleClearPromptCache = async () => {
    setClearingCache(true);
    setCacheCleared(false);
    try {
      await api.clearPromptCache();
      setCacheCleared(true);
      setTimeout(() => setCacheCleared(false), 3000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to clear prompt cache');
    } finally {
      setClearingCache(false);
    }
  };

  // ─── Refresh Pricing Handler ────────────────────────────
  const handleRefreshPricing = async () => {
    setRefreshingPricing(true);
    setError(null);
    setPricingDebug(null);
    try {
      const pricingResult = await api.getModelPricing(true);
      const apiPricing = pricingResult.pricing || {};
      // Merge: stored model pricing takes precedence over API pricing
      const merged: Record<string, ModelPricingEntry> = { ...apiPricing };
      for (const m of models) {
        if (m.input_cost_per_1k != null || m.output_cost_per_1k != null) {
          merged[m.model_id] = {
            model_id: m.model_id,
            input_cost_per_1k: m.input_cost_per_1k ?? apiPricing[m.model_id]?.input_cost_per_1k ?? null,
            output_cost_per_1k: m.output_cost_per_1k ?? apiPricing[m.model_id]?.output_cost_per_1k ?? null,
          };
        }
      }
      setPricing(merged);
      // Show debug info if there are unmatched models
      if (pricingResult.unmatched_models || pricingResult.available_pricing_names) {
        setPricingDebug({
          unmatched_models: pricingResult.unmatched_models,
          available_pricing_names: pricingResult.available_pricing_names,
        });
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to refresh pricing from AWS');
    } finally {
      setRefreshingPricing(false);
    }
  };

  // ─── Assignment Handlers ────────────────────────────────
  const handleAssignmentChange = async (functionId: string, modelId: string) => {
    setSavingAssignment(functionId);
    setError(null);
    try {
      await api.updateModelAssignment(functionId, { model_id: modelId });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update assignment');
    } finally {
      setSavingAssignment(null);
    }
  };

  // ─── Model Pricing Handlers ─────────────────────────────
  const handlePricingSave = async (model: AIModel, field: 'input_cost_per_1k' | 'output_cost_per_1k', value: string) => {
    setError(null);
    try {
      const numVal = parseFloat(value);
      await api.updateModel(model.model_id, {
        display_name: model.display_name,
        enabled: model.enabled,
        [field]: isNaN(numVal) ? null : numVal,
      });
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update pricing');
    }
  };

  // ─── Cost Config Handlers ───────────────────────────────
  const handleSaveCostConfig = async (newConfig: CostConfig) => {
    setSavingCostConfig(true);
    setError(null);
    try {
      await api.updateModelAssignment('_cost_config', { ...newConfig });
      setCostConfig(newConfig);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to save cost config');
    } finally {
      setSavingCostConfig(false);
    }
  };

  const handleCostConfigChange = (field: keyof CostConfig, value: string) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    const newConfig = { ...costConfig, [field]: num };
    setCostConfig(newConfig);
    handleSaveCostConfig(newConfig);
  };

  // ─── Function Cost Field Handlers ───────────────────────
  const handleFunctionCostFieldSave = async (functionId: string, field: string, value: string) => {
    setSavingAssignment(functionId);
    setError(null);
    try {
      const data: Record<string, string | number> = {};
      if (field === 'ai_pct') {
        data.ai_pct = parseFloat(value) || 0;
      } else {
        data[field] = value;
      }
      await api.updateModelAssignment(functionId, data);
      await loadData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr?.response?.data?.message || 'Failed to update function cost field');
    } finally {
      setSavingAssignment(null);
      setEditingField(null);
    }
  };

  const startEditing = (functionId: string, field: string, currentValue: string) => {
    setEditingField({ functionId, field });
    setEditFieldValue(currentValue);
  };

  // ─── Prompt Editor Handlers ────────────────────────────
  const selectVersion = (version: any) => {
    setSelectedVersion(version);
    setEditingPrompt({ ...version });
    setPromptWarnings([]);
    cleanSnapshotRef.current = JSON.stringify(version);
  };

  // Track "clean" state for dirty detection
  const cleanSnapshotRef = useRef<string | null>(null);

  const isPromptDirty = useCallback((): boolean => {
    if (!editingPrompt) return false;
    if (cleanSnapshotRef.current === null) return true; // new unsaved version
    return JSON.stringify(editingPrompt) !== cleanSnapshotRef.current;
  }, [editingPrompt]);

  const closePromptEditor = useCallback(() => {
    if (isPromptDirty()) {
      if (!window.confirm('You have unsaved changes. Close without saving?')) return;
    }
    setPromptEditorOpen(false);
    setPromptError(null);
  }, [isPromptDirty]);

  // ESC key closes prompt editor
  useEffect(() => {
    if (!promptEditorOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closePromptEditor();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [promptEditorOpen, closePromptEditor]);

  const incrementPatch = (version: string): string => {
    const parts = version.split('.');
    if (parts.length === 3) {
      parts[2] = String(parseInt(parts[2] || '0') + 1);
      return parts.join('.');
    }
    return version + '.1';
  };

  const openPromptEditor = async (functionId: string) => {
    const promptName = FUNCTION_TO_PROMPT[functionId];
    if (!promptName) return;
    // Clear stale data immediately so previous prompt doesn't flash
    setSelectedPromptName(promptName);
    setPromptVersions([]);
    setSelectedVersion(null);
    setEditingPrompt(null);
    setVersionPanelOpen(window.innerWidth >= 768);
    setPromptEditorOpen(true);
    setPromptError(null);
    setPromptLoading(true);
    // Populate function config from assignments
    setLightboxFunctionId(functionId);
    const assignment = assignments.find(a => a.function_id === functionId);
    if (assignment) {
      setLightboxDescription(assignment.description || '');
      setLightboxModelId(assignment.model_id || '');
      setLightboxMaxTokens(assignment.max_tokens ? String(assignment.max_tokens) : '');
      setLightboxPostProcessors(assignment.post_processors ? JSON.parse(JSON.stringify(assignment.post_processors)).map((p: PostProcessorConfig) => ({ ...p, params: p.params || {} })) : []);
    } else {
      setLightboxDescription('');
      setLightboxModelId('');
      setLightboxMaxTokens('');
      setLightboxPostProcessors([]);
    }
    try {
      const data = await api.getPromptVersions(promptName);
      const versions = data.prompts || [];
      setPromptVersions(versions);
      const prodVersions = versions.filter((p: any) => p.prompt_state === 'prod');
      if (prodVersions.length > 0) {
        selectVersion(prodVersions[0]);
      } else if (versions.length > 0) {
        selectVersion(versions[0]);
      } else {
        setSelectedVersion(null);
        setEditingPrompt(null);
      }
    } catch (err: any) {
      setPromptError(err?.response?.data?.message || 'Failed to load prompt versions');
    } finally {
      setPromptLoading(false);
    }
  };

  const openPromptByName = async (promptName: string) => {
    // Clear stale data immediately so previous prompt doesn't flash
    setSelectedPromptName(promptName);
    setPromptVersions([]);
    setSelectedVersion(null);
    setEditingPrompt(null);
    setVersionPanelOpen(window.innerWidth >= 768);
    setPromptEditorOpen(true);
    setPromptError(null);
    setPromptLoading(true);
    // Snippets don't have function assignments
    setLightboxFunctionId('');
    setLightboxDescription('');
    setLightboxModelId('');
    setLightboxMaxTokens('');
    setLightboxPostProcessors([]);
    try {
      const data = await api.getPromptVersions(promptName);
      const versions = data.prompts || [];
      setPromptVersions(versions);
      const prodVersions = versions.filter((p: any) => p.prompt_state === 'prod');
      if (prodVersions.length > 0) {
        selectVersion(prodVersions[0]);
      } else if (versions.length > 0) {
        selectVersion(versions[0]);
      } else {
        setSelectedVersion(null);
        setEditingPrompt(null);
      }
    } catch (err: any) {
      setPromptError(err?.response?.data?.message || 'Failed to load prompt versions');
    } finally {
      setPromptLoading(false);
    }
  };

  const deleteVersion = async (v: any) => {
    const confirmed = window.confirm(`Delete version ${v.prompt_version} (${v.prompt_state}) of "${v.prompt_name}"?`);
    if (!confirmed) return;
    try {
      await api.deletePromptVersion(v.prompt_name, v.prompt_version);
      // Remove from local list
      const updated = promptVersions.filter((p: any) => !(p.prompt_name === v.prompt_name && p.prompt_version === v.prompt_version));
      setPromptVersions(updated);
      // Clear editor if we deleted the selected version
      if (selectedVersion?.prompt_version === v.prompt_version && selectedVersion?.prompt_name === v.prompt_name) {
        setSelectedVersion(null);
        setEditingPrompt(null);
      }
    } catch (err: any) {
      setPromptError(err.response?.data?.error || 'Failed to delete version');
    }
  };

  const addNewVersion = () => {
    const clone = editingPrompt
      ? {
          ...editingPrompt,
          prompt_state: 'test',
          traffic_pct: '0',
          prompt_version: incrementPatch(editingPrompt.prompt_version || '1.0.0'),
        }
      : {
          prompt_name: selectedPromptName || '',
          prompt_version: '1.0.0',
          prompt_type: 'generate',
          prompt_state: 'test',
          traffic_pct: '0',
          prompt_text: '',
          temperature: '',
        };
    setEditingPrompt(clone);
    setSelectedVersion(null);
    cleanSnapshotRef.current = null; // mark as unsaved new version
    setFlashNewVersion(true);
    setTimeout(() => setFlashNewVersion(false), 1200);
  };

  const savePrompt = async () => {
    if (!editingPrompt) return;

    let versionToSave = editingPrompt.prompt_version;
    if (autoIncrementPatch && selectedVersion) {
      versionToSave = incrementPatch(editingPrompt.prompt_version);
    }

    const existing = promptVersions.find(
      (p: any) => p.prompt_version === versionToSave && p.prompt_name === editingPrompt.prompt_name
    );
    if (existing && (!selectedVersion || selectedVersion.prompt_version !== versionToSave)) {
      setPromptError(`Version ${versionToSave} already exists for ${editingPrompt.prompt_name}`);
      return;
    }

    // Governance pre-flight — same rules the server enforces
    // (admin/prompt_validation.py). Fail here so a bad config never leaves the
    // browser, and so the function-config PUT isn't applied when the prompt PUT
    // would be rejected.
    const check = validatePromptPayload(
      { ...editingPrompt, prompt_version: versionToSave },
      selectedVersion?.prompt_type,
    );
    if (check.error) {
      setPromptError(check.error);
      setPromptWarnings([]);
      return;
    }
    setPromptWarnings(check.warnings);

    setPromptSaving(true);
    setPromptError(null);
    try {
      // Save function config if this is a function (not a snippet)
      if (lightboxFunctionId) {
        const assignment = assignments.find(a => a.function_id === lightboxFunctionId);
        const postProcessorsChanged =
          JSON.stringify(assignment?.post_processors || []) !== JSON.stringify(lightboxPostProcessors);
        const configChanged =
          (assignment?.description || '') !== lightboxDescription ||
          (assignment?.model_id || '') !== lightboxModelId ||
          String(assignment?.max_tokens || '') !== lightboxMaxTokens ||
          postProcessorsChanged;
        if (configChanged) {
          const updateData: Record<string, unknown> = {
            model_id: lightboxModelId,
            description: lightboxDescription,
          };
          const parsedTokens = parseInt(lightboxMaxTokens);
          if (!isNaN(parsedTokens) && parsedTokens > 0) {
            updateData.max_tokens = parsedTokens;
          }
          if (postProcessorsChanged) {
            updateData.post_processors = lightboxPostProcessors;
          }
          await api.updateModelAssignment(lightboxFunctionId, updateData);
          // Update local assignments state immediately
          setAssignments(prev => prev.map(a =>
            a.function_id === lightboxFunctionId
              ? { ...a, description: lightboxDescription, model_id: lightboxModelId, max_tokens: !isNaN(parsedTokens) && parsedTokens > 0 ? parsedTokens : null, post_processors: lightboxPostProcessors }
              : a
          ));
        }
      }

      const saveResult = await api.updatePrompt(editingPrompt.prompt_name, {
        ...editingPrompt,
        prompt_version: versionToSave,
      });
      const data = await api.getPromptVersions(editingPrompt.prompt_name);
      setPromptVersions(data.prompts || []);
      const saved = (data.prompts || []).find((p: any) => p.prompt_version === versionToSave);
      if (saved) selectVersion(saved);
      // Server is authoritative on warnings too. Set AFTER selectVersion, which
      // clears the warning strip when a version is loaded into the editor.
      if (Array.isArray(saveResult?.warnings) && saveResult.warnings.length) {
        setPromptWarnings(saveResult.warnings);
      }
    } catch (err: any) {
      setPromptError(err?.response?.data?.message || 'Failed to save prompt');
    }
    setPromptSaving(false);
  };

  const filteredPromptVersions = useMemo(() => {
    return promptVersions.filter((p: any) => {
      if (promptFilter.type && p.prompt_type !== promptFilter.type) return false;
      if (promptFilter.state && p.prompt_state !== promptFilter.state) return false;
      return true;
    });
  }, [promptVersions, promptFilter]);

  const stateColor = (state: string): string => {
    switch (state) {
      case 'prod': return 'bg-green-100 text-green-700 border-green-300';
      case 'test': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'expired': return 'bg-gray-100 text-gray-500 border-gray-300';
      default: return 'bg-gray-100 text-gray-500 border-gray-300';
    }
  };

  // ─── Snippet Management Handlers ────────────────────────
  const handleAddSnippet = async () => {
    const name = newSnippetName.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!name) return;
    try {
      await api.updatePrompt(name, {
        prompt_name: name,
        prompt_version: '1.0.0',
        prompt_type: 'snippet',
        prompt_state: 'prod',
        prompt_text: '(new snippet — edit content here)',
      });
      setNewSnippetName('');
      setAddingSnippet(false);
      // Refresh snippets list
      const allPrompts = await api.listPrompts({ prompt_type: 'snippet' });
      setSnippets((allPrompts.prompts || []).sort((a: { prompt_name: string }, b: { prompt_name: string }) => a.prompt_name.localeCompare(b.prompt_name)));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to create snippet');
    }
  };

  const handleDeleteSnippet = async (snippetName: string) => {
    if (!window.confirm(`Delete snippet "${snippetName}" and all its versions? This cannot be undone.`)) return;
    setDeletingSnippet(snippetName);
    try {
      await api.deletePrompt(snippetName);
      // Refresh snippets list
      const allPrompts = await api.listPrompts({ prompt_type: 'snippet' });
      setSnippets((allPrompts.prompts || []).sort((a: { prompt_name: string }, b: { prompt_name: string }) => a.prompt_name.localeCompare(b.prompt_name)));
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete snippet');
    }
    setDeletingSnippet(null);
  };

  // ─── Calculated Values ──────────────────────────────────
  const costPerJob = useMemo(() => {
    return assignments.reduce((sum, a) => sum + calcCostPerRequest(a, costConfig, pricing), 0);
  }, [assignments, costConfig, pricing]);

  const costPerUserPerMonth = useMemo(() => {
    return costPerJob * costConfig.jobs_per_month;
  }, [costPerJob, costConfig.jobs_per_month]);

  const formatCost = (cost: number | null | undefined): string => {
    if (cost === null || cost === undefined) return 'N/A';
    if (cost === 0) return '$0';
    return `$${cost.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')}`;
  };

  const formatCostShort = (cost: number): string => {
    if (cost === 0) return '$0';
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    return `$${cost.toFixed(4)}`;
  };

  const enabledModels = models.filter((m) => m.enabled);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Brain className="w-7 h-7 text-violet-500" />
          <h1 className="text-2xl font-bold text-gray-800">AI Configuration</h1>
        </div>
        <div className="flex items-center gap-2">
          {models.length === 0 && !loading && (
            <button
              onClick={handleSeedData}
              disabled={seeding}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
            >
              {seeding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Seed Models
            </button>
          )}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && models.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
        </div>
      ) : models.length === 0 ? (
        <div className="text-center py-20">
          <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">No Models Configured</h2>
          <p className="text-gray-500 mb-6">
            Click "Seed Models" to load the default Bedrock models, or add models manually below.
          </p>
        </div>
      ) : null}

      {/* Section A: Model Registry */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Available Models (Model Registry)</h2>
              <p className="text-sm text-gray-500 mt-1">Manage the AI models available for generation functions. Pricing is auto-fetched from AWS and cached — edit fields to override.</p>
            </div>
            <button
              onClick={handleRefreshPricing}
              disabled={refreshingPricing}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors whitespace-nowrap"
              title="Force refresh pricing from AWS Pricing API"
            >
              {refreshingPricing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
              Refresh Pricing
            </button>
          </div>
        </div>

        {/* Pricing Debug Info Banner */}
        {pricingDebug && pricingDebug.unmatched_models && pricingDebug.unmatched_models.length > 0 && (
          <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  {pricingDebug.unmatched_models.length} registered model{pricingDebug.unmatched_models.length !== 1 ? 's' : ''} did not match AWS pricing
                </span>
              </div>
              <button onClick={() => setPricingDebug(null)} className="text-amber-400 hover:text-amber-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-amber-700 mt-1 space-y-0.5">
              {pricingDebug.unmatched_models.map((m) => (
                <div key={m.model_id}>
                  <code className="bg-amber-100 px-1 rounded">{m.display_name}</code>
                  <span className="text-amber-500 ml-1">({m.model_id})</span>
                </div>
              ))}
            </div>
            {pricingDebug.available_pricing_names && (
              <details className="mt-2">
                <summary className="text-xs text-amber-600 cursor-pointer hover:text-amber-800">
                  Show {pricingDebug.available_pricing_names.length} available AWS pricing names
                </summary>
                <div className="mt-1 text-xs text-amber-600 max-h-40 overflow-y-auto">
                  {pricingDebug.available_pricing_names.map((name) => (
                    <div key={name}><code className="bg-amber-100/50 px-1 rounded">{name}</code></div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Add Model Search */}
        <div className="p-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-end gap-3">
            <div className="w-36">
              <label className="block text-xs font-medium text-gray-600 mb-1">Model Type</label>
              <select
                value={addModelType}
                onChange={(e) => setAddModelType(e.target.value as 'llm' | 'embedding')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="llm">LLM</option>
                <option value="embedding">Embedding</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search Bedrock Models</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchResults([]); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Type a model name or ID (e.g. claude, nova, llama)..."
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchModels(); }}
              />
            </div>
            <button
              onClick={handleSearchModels}
              disabled={!searchQuery.trim() || searchQuery.trim().length < 2 || searching}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
              title="Search Bedrock for matching models"
            >
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add Model
            </button>
          </div>
          {/* Search Results — click to add */}
          {searchResults.length > 0 && (
            <div className="mt-3 bg-white border border-primary-200 rounded-lg max-h-64 overflow-y-auto">
              <div className="px-3 py-2 text-xs font-semibold text-primary-700 bg-primary-50 border-b border-primary-200 sticky top-0">
                Found {searchResults.length} available model{searchResults.length !== 1 ? 's' : ''} — click to add:
              </div>
              {searchResults.map((match) => (
                <button
                  key={match.model_id}
                  onClick={() => handleSelectAndAddModel(match)}
                  disabled={addingModel === match.model_id}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-primary-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-3 disabled:opacity-50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {addingModel === match.model_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-500 flex-shrink-0" />
                      ) : (
                        <Plus className="w-3.5 h-3.5 text-primary-400 flex-shrink-0" />
                      )}
                      <span className="font-medium text-gray-800">{match.model_name}</span>
                      <span className="text-gray-500 text-xs">({match.provider})</span>
                    </div>
                    <code className="text-xs text-gray-500 ml-5.5 block mt-0.5 truncate">{match.model_id}</code>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 text-xs font-mono text-gray-500">
                    <span title="Input cost per 1K tokens">
                      In: {match.input_cost_per_1k != null ? `$${match.input_cost_per_1k}` : 'N/A'}
                    </span>
                    <span title="Output cost per 1K tokens">
                      Out: {match.output_cost_per_1k != null ? `$${match.output_cost_per_1k}` : 'N/A'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Models Table */}
        {models.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Display Name</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase px-3 py-3">Type</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-5 py-3">Model ID (Bedrock)</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">Max Tokens</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">$/1K In</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">$/1K Out</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase px-5 py-3">Enabled</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((model) => (
                  <tr key={model.model_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    {/* Display Name */}
                    <td className="px-5 py-3">
                      {editingModel === model.model_id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editDisplayName}
                            onChange={(e) => setEditDisplayName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-primary-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditSave(model);
                              if (e.key === 'Escape') setEditingModel(null);
                            }}
                          />
                          <button
                            onClick={() => handleEditSave(model)}
                            className="p-1 text-green-600 hover:text-green-700"
                            title="Save"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingModel(null)}
                            className="p-1 text-gray-500 hover:text-gray-600"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span
                          className="font-medium text-gray-800 cursor-pointer hover:text-primary-600"
                          onClick={() => {
                            setEditingModel(model.model_id);
                            setEditDisplayName(model.display_name);
                          }}
                          title="Click to edit"
                        >
                          {model.display_name}
                        </span>
                      )}
                    </td>

                    {/* Type Badge */}
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        (model.model_type || 'llm') === 'embedding'
                          ? 'bg-cyan-100 text-cyan-700 border border-cyan-200'
                          : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                      }`}>
                        {(model.model_type || 'llm').toUpperCase()}
                      </span>
                    </td>

                    {/* Model ID */}
                    <td className="px-5 py-3">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                        {model.model_id}
                      </code>
                    </td>

                    {/* Max Tokens */}
                    <td className="px-5 py-3 text-right">
                      <span className="text-sm font-mono text-gray-600">
                        {model.model_id.includes('anthropic') ? '64,000' :
                         model.model_id.includes('nova-micro') ? '5,120' :
                         model.model_id.includes('nova-lite') || model.model_id.includes('nova-2-lite') ? '5,120' :
                         model.model_id.includes('nova-pro') ? '10,240' :
                         '—'}
                      </span>
                    </td>

                    {/* Pricing In */}
                    <td className="px-5 py-2 text-right">
                      <input
                        type="number"
                        step="0.000001"
                        defaultValue={pricing[model.model_id]?.input_cost_per_1k ?? ''}
                        onBlur={(e) => handlePricingSave(model, 'input_cost_per_1k', e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        className="w-24 px-2 py-1 border border-gray-200 rounded text-xs font-mono text-right focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="N/A"
                        title="Cost per 1K input tokens — editable"
                      />
                    </td>
                    {/* Pricing Out */}
                    <td className="px-5 py-2 text-right">
                      <input
                        type="number"
                        step="0.000001"
                        defaultValue={pricing[model.model_id]?.output_cost_per_1k ?? ''}
                        onBlur={(e) => handlePricingSave(model, 'output_cost_per_1k', e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                        className="w-24 px-2 py-1 border border-gray-200 rounded text-xs font-mono text-right focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="N/A"
                        title="Cost per 1K output tokens — editable"
                      />
                    </td>

                    {/* Enabled Toggle */}
                    <td className="px-5 py-3 text-center">
                      <button
                        onClick={() => handleToggleEnabled(model)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          model.enabled
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {model.enabled ? (
                          <>
                            <ToggleRight className="w-4 h-4" />
                            Enabled
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-4 h-4" />
                            Disabled
                          </>
                        )}
                      </button>
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleTestModel(model.model_id)}
                          disabled={testingModel === model.model_id}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 rounded-lg hover:bg-violet-100 disabled:opacity-50 transition-colors"
                          title="Test model with a joke prompt"
                        >
                          {testingModel === model.model_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          Test
                        </button>
                        <button
                          onClick={() => {
                            setEditingModel(model.model_id);
                            setEditDisplayName(model.display_name);
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model.model_id)}
                          className="p-1.5 text-gray-500 hover:text-red-500 transition-colors"
                          title="Delete model"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Test Result */}
      {testResult && (
        <div className="mb-6 bg-violet-50 border border-violet-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Play className="w-4 h-4 text-violet-600" />
              <span className="font-semibold text-violet-800 text-sm">
                Test Result: <code className="bg-violet-100 px-1.5 py-0.5 rounded">{testResult.modelId}</code>
              </span>
            </div>
            <button onClick={() => setTestResult(null)} className="text-violet-400 hover:text-violet-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-violet-700 whitespace-pre-wrap">{testResult.response}</p>
        </div>
      )}

      {/* Section B: Cost Assumptions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Cost Assumptions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Admin planning estimates for AI cost modeling. These are not operational — for estimation only.
          </p>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Sub-section 1: Assumed Tokens per Data Source */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Assumed Tokens per Data Source</h3>
            <p className="text-xs text-gray-500 mb-3">Define token variables used in function cost formulas below.</p>
            <div className="space-y-3">
              {([
                { key: 'job_desc' as const, label: '{job_desc}', desc: 'Job description tokens' },
                { key: 'resume' as const, label: '{resume}', desc: 'Full resume tokens' },
                { key: 'small_section' as const, label: '{small_section}', desc: 'Single section tokens' },
                { key: 'experience_section' as const, label: '{experience_section}', desc: 'Experience section tokens' },
                { key: 'resume_tags' as const, label: '{resume_tags}', desc: 'Resume tag summary tokens' },
                { key: 'job_tags' as const, label: '{job_tags}', desc: 'Job desc tag summary tokens' },
              ]).map(({ key, label, desc }) => (
                <div key={key} className="flex items-center gap-3">
                  <code className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded font-mono w-32 text-center">{label}</code>
                  <input
                    type="number"
                    value={costConfig[key]}
                    onChange={(e) => handleCostConfigChange(key, e.target.value)}
                    disabled={savingCostConfig}
                    className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                    min={0}
                  />
                  <span className="text-xs text-gray-500">{desc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sub-section 2: Assumed Cost per User per Month */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Assumed Cost per User per Month</h3>
            <p className="text-xs text-gray-500 mb-3">Calculated from function assignments and model pricing.</p>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Final Cost per Job</span>
                <span className="font-mono text-sm font-semibold text-gray-800">{formatCostShort(costPerJob)}</span>
              </div>
              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                <span className="text-sm text-gray-600">Jobs per User per Month</span>
                <input
                  type="number"
                  value={costConfig.jobs_per_month}
                  onChange={(e) => handleCostConfigChange('jobs_per_month', e.target.value)}
                  disabled={savingCostConfig}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                  min={0}
                />
              </div>
              <div className="flex items-center justify-between py-2 px-3 bg-violet-50 rounded border border-violet-200">
                <span className="text-sm font-semibold text-violet-700">Final Cost per User per Month</span>
                <span className="font-mono text-sm font-bold text-violet-800">{formatCostShort(costPerUserPerMonth)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section C: Function-to-Model Assignments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Prompt Controls</h2>
            <p className="text-sm text-gray-500 mt-1">
              Assign which AI model each generation function uses. Cost fields use token variables from Cost Assumptions above.
            </p>
          </div>
          <button
            onClick={handleClearPromptCache}
            disabled={clearingCache}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
              cacheCleared
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
            title="Invalidate prompt cache across all Lambda instances"
          >
            {clearingCache ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : cacheCleared ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            {cacheCleared ? 'Cache Cleared' : 'Clear Prompt Cache'}
          </button>
        </div>

        {assignments.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-4 py-3">AI Task</th>
                  <th className="text-center text-xs font-semibold text-gray-500 uppercase px-3 py-3 w-20">% AI</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Token In / Request</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3">Token Out / Request</th>
                  <th className="text-right text-xs font-semibold text-gray-500 uppercase px-3 py-3 w-28">Cost/Request</th>
                  <th className="text-left text-xs font-semibold text-gray-500 uppercase px-3 py-3 min-w-[200px]">Assigned Model</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => {
                  const costPerReq = calcCostPerRequest(assignment, costConfig, pricing);
                  return (
                    <tr key={assignment.function_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      {/* Function ID / AI Task */}
                      <td className="px-4 py-2">
                        {FUNCTION_TO_PROMPT[assignment.function_id] ? (
                          <code
                            className="text-xs font-medium text-blue-600 bg-gray-100 px-2 py-0.5 rounded cursor-pointer hover:underline hover:text-blue-800"
                            onClick={() => openPromptEditor(assignment.function_id)}
                            title={`Edit prompt: ${FUNCTION_TO_PROMPT[assignment.function_id]}`}
                          >
                            {assignment.function_id}
                          </code>
                        ) : (
                          <code className="text-xs font-medium text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                            {assignment.function_id}
                          </code>
                        )}
                      </td>

                      {/* % AI */}
                      <td className="px-3 py-2 text-center">
                        {editingField?.functionId === assignment.function_id && editingField?.field === 'ai_pct' ? (
                          <input
                            type="number"
                            value={editFieldValue}
                            onChange={(e) => setEditFieldValue(e.target.value)}
                            onBlur={() => handleFunctionCostFieldSave(assignment.function_id, 'ai_pct', editFieldValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFunctionCostFieldSave(assignment.function_id, 'ai_pct', editFieldValue);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            className="w-16 px-1 py-0.5 border border-primary-300 rounded text-xs text-center font-mono focus:ring-2 focus:ring-primary-500"
                            autoFocus
                            min={0}
                            max={100}
                          />
                        ) : (
                          <span
                            className="text-xs font-mono text-gray-700 cursor-pointer hover:text-primary-600 hover:bg-primary-50 px-2 py-0.5 rounded"
                            onClick={() => startEditing(assignment.function_id, 'ai_pct', String(assignment.ai_pct ?? 0))}
                            title="Click to edit"
                          >
                            {assignment.ai_pct ?? 0}%
                          </span>
                        )}
                      </td>

                      {/* Token In / Request */}
                      <td className="px-3 py-2">
                        {editingField?.functionId === assignment.function_id && editingField?.field === 'token_in_formula' ? (
                          <input
                            type="text"
                            value={editFieldValue}
                            onChange={(e) => setEditFieldValue(e.target.value)}
                            onBlur={() => handleFunctionCostFieldSave(assignment.function_id, 'token_in_formula', editFieldValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFunctionCostFieldSave(assignment.function_id, 'token_in_formula', editFieldValue);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            className="w-full px-2 py-0.5 border border-primary-300 rounded text-xs font-mono focus:ring-2 focus:ring-primary-500"
                            autoFocus
                          />
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-primary-50 px-2 py-0.5 rounded"
                            onClick={() => startEditing(assignment.function_id, 'token_in_formula', assignment.token_in_formula || '')}
                            title="Click to edit"
                          >
                            <code className="text-xs font-mono text-amber-700">{assignment.token_in_formula || '—'}</code>
                            <span className="text-xs text-gray-500 ml-2">({resolveFormula(assignment.token_in_formula, costConfig)})</span>
                          </div>
                        )}
                      </td>

                      {/* Token Out / Request */}
                      <td className="px-3 py-2">
                        {editingField?.functionId === assignment.function_id && editingField?.field === 'token_out_formula' ? (
                          <input
                            type="text"
                            value={editFieldValue}
                            onChange={(e) => setEditFieldValue(e.target.value)}
                            onBlur={() => handleFunctionCostFieldSave(assignment.function_id, 'token_out_formula', editFieldValue)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleFunctionCostFieldSave(assignment.function_id, 'token_out_formula', editFieldValue);
                              if (e.key === 'Escape') setEditingField(null);
                            }}
                            className="w-full px-2 py-0.5 border border-primary-300 rounded text-xs font-mono focus:ring-2 focus:ring-primary-500"
                            autoFocus
                          />
                        ) : (
                          <div
                            className="cursor-pointer hover:bg-primary-50 px-2 py-0.5 rounded"
                            onClick={() => startEditing(assignment.function_id, 'token_out_formula', assignment.token_out_formula || '')}
                            title="Click to edit"
                          >
                            <code className="text-xs font-mono text-amber-700">{assignment.token_out_formula || '—'}</code>
                            <span className="text-xs text-gray-500 ml-2">({resolveFormula(assignment.token_out_formula, costConfig)})</span>
                          </div>
                        )}
                      </td>

                      {/* Cost/Request (calculated) */}
                      <td className="px-3 py-2 text-right">
                        <span className={`text-xs font-mono font-semibold ${costPerReq > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                          {formatCost(costPerReq)}
                        </span>
                      </td>

                      {/* Assigned Model */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <select
                            value={assignment.model_id}
                            onChange={(e) => handleAssignmentChange(assignment.function_id, e.target.value)}
                            disabled={savingAssignment === assignment.function_id}
                            className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                          >
                            {!enabledModels.find((m) => m.model_id === assignment.model_id) && assignment.model_id && (
                              <option value={assignment.model_id}>
                                {models.find((m) => m.model_id === assignment.model_id)?.display_name || assignment.model_id} (disabled)
                              </option>
                            )}
                            {enabledModels.map((model) => (
                              <option key={model.model_id} value={model.model_id}>
                                {model.display_name}
                              </option>
                            ))}
                          </select>
                          {savingAssignment === assignment.function_id && (
                            <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && (
            <div className="p-8 text-center text-gray-500">
              <p>No function assignments found. Seed the models first to create default assignments.</p>
            </div>
          )
        )}
      </div>

      {/* ─── AI Snippets and Parameters ────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Info className="w-5 h-5 text-indigo-500" />
            AI Snippets and Parameters
          </h2>
          <p className="text-sm text-gray-500 mt-1">Reusable prompt snippets and parameter reference</p>
        </div>
        <div className="grid grid-cols-2 gap-0">
          {/* Left: Snippets */}
          <div className="border-r border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">Snippets</h3>
              <button
                onClick={() => setAddingSnippet(true)}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded hover:bg-violet-100 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
            {addingSnippet && (
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="text"
                  value={newSnippetName}
                  onChange={(e) => setNewSnippetName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddSnippet(); if (e.key === 'Escape') { setAddingSnippet(false); setNewSnippetName(''); } }}
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs font-mono focus:ring-2 focus:ring-primary-500"
                  placeholder="snippet_name"
                  autoFocus
                />
                <button onClick={handleAddSnippet} disabled={!newSnippetName.trim()} className="px-2 py-1.5 text-xs font-medium text-white bg-violet-600 rounded hover:bg-violet-700 disabled:opacity-50">
                  Create
                </button>
                <button onClick={() => { setAddingSnippet(false); setNewSnippetName(''); }} className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              </div>
            )}
            {snippets.length > 0 ? (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Snippet</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Placeholder</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(new Map(snippets.map(s => [s.prompt_name, s])).values()).map((snippet) => {
                    const placeholder = `{{${snippet.prompt_name.toUpperCase()}}}`;
                    return (
                      <tr key={snippet.prompt_name} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2">
                          <button
                            onClick={() => openPromptByName(snippet.prompt_name)}
                            className="text-sm font-mono text-violet-700 hover:text-violet-900 bg-violet-50 hover:bg-violet-100 px-2 py-0.5 rounded transition-colors cursor-pointer"
                          >
                            {snippet.prompt_name}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-left">
                          <button
                            onClick={() => copyToClipboard(placeholder)}
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded transition-colors cursor-pointer"
                            title="Click to copy"
                          >
                            {copiedText === placeholder ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                            {placeholder}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleDeleteSnippet(snippet.prompt_name)}
                            disabled={deletingSnippet === snippet.prompt_name}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
                            title="Delete snippet"
                          >
                            {deletingSnippet === snippet.prompt_name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-500">No snippets found. Seed prompts to create defaults.</p>
            )}
          </div>

          {/* Right: Parameters Reference */}
          <div className="p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Parameters Reference</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Placeholder</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2">Description</th>
                </tr>
              </thead>
              <tbody>
                {[...PROMPT_PARAMETERS].sort((a, b) => a.name.localeCompare(b.name)).map((param) => (
                  <tr key={param.name} className="border-b border-gray-100">
                    <td className="px-3 py-2 text-left">
                      <button
                        onClick={() => copyToClipboard(param.name)}
                        className="inline-flex items-center gap-1.5 text-xs font-mono text-amber-600 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors cursor-pointer"
                        title="Click to copy"
                      >
                        {copiedText === param.name ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                        {param.name}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600">{param.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ─── Prompt Editor Modal ──────────────────────────────── */}
      {promptEditorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closePromptEditor}
          />
          {/* Panel */}
          <div className="relative bg-white rounded-xl shadow-2xl max-w-[90vw] w-full mx-4 max-h-[95vh] min-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-violet-500" />
                <h2 className="text-lg font-semibold text-gray-800">
                  Prompt Editor: <code className="bg-violet-50 text-violet-700 px-2 py-0.5 rounded text-sm">{selectedPromptName}</code>
                </h2>
              </div>
              <button
                onClick={closePromptEditor}
                className="p-1 text-gray-500 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Error */}
            {promptError && (
              <div className="mx-5 mt-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{promptError}</span>
                </div>
                <button onClick={() => setPromptError(null)} className="text-red-400 hover:text-red-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Non-blocking governance warnings (prompt-cache cost, etc.) */}
            {promptWarnings.length > 0 && (
              <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-start justify-between gap-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <ul className="text-sm space-y-1">
                    {promptWarnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
                <button onClick={() => setPromptWarnings([])} className="text-amber-400 hover:text-amber-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Body */}
            <div className="flex flex-1 overflow-hidden">
              {/* Left Panel: Version Selector (collapsible, collapsed by default on mobile) */}
              {versionPanelOpen ? (
                <div className="w-64 min-w-[16rem] border-r border-gray-200 flex flex-col bg-gray-50">
                  {/* Panel Header with Collapse Toggle */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Versions</span>
                    <button
                      onClick={() => setVersionPanelOpen(false)}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Collapse version panel"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Filters */}
                  <div className="p-3 border-b border-gray-200 space-y-2">
                    <select
                      value={promptFilter.type}
                      onChange={(e) => setPromptFilter((f) => ({ ...f, type: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">All Types</option>
                      {PROMPT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                      value={promptFilter.state}
                      onChange={(e) => setPromptFilter((f) => ({ ...f, state: e.target.value }))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">All States</option>
                      {PROMPT_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Version List */}
                  <div className="flex-1 overflow-y-auto">
                    {promptLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                      </div>
                    ) : filteredPromptVersions.length === 0 ? (
                      <div className="p-4 text-center text-xs text-gray-500">
                        No versions found
                      </div>
                    ) : (
                      filteredPromptVersions.map((v: any, idx: number) => (
                        <div
                          key={`${v.prompt_version}-${v.prompt_type}-${idx}`}
                          className={`group relative w-full text-left px-3 py-2.5 border-b border-gray-200 hover:bg-white transition-colors cursor-pointer ${
                            selectedVersion?.prompt_version === v.prompt_version && selectedVersion?.prompt_type === v.prompt_type
                              ? 'bg-white border-l-2 border-l-violet-500'
                              : ''
                          }`}
                          onClick={() => selectVersion(v)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-mono font-medium text-gray-800">v{v.prompt_version}</span>
                            <div className="flex items-center gap-1">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${stateColor(v.prompt_state)}`}>
                                {v.prompt_state}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); deleteVersion(v); }}
                                className="hidden group-hover:block p-0.5 text-gray-300 hover:text-red-500 transition-colors"
                                title={`Delete v${v.prompt_version}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            {v.prompt_type} | traffic: {formatTrafficPct(v.traffic_pct)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add New Button */}
                  <div className="p-3 border-t border-gray-200">
                    <button
                      onClick={addNewVersion}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add New Version
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-r border-gray-200 flex flex-col items-center bg-gray-50 py-2">
                  <button
                    onClick={() => setVersionPanelOpen(true)}
                    className="p-1.5 text-gray-400 hover:text-violet-600 transition-colors"
                    title="Show version panel"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Right Panel: Edit Form */}
              <div className="flex-1 overflow-y-auto p-5">
                {editingPrompt ? (
                  <div className="space-y-3">
                    {/* Row 1: Version, Type, State, Traffic % */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className={`rounded-lg transition-all duration-700 ${flashNewVersion ? 'ring-2 ring-violet-400 bg-violet-50' : ''}`}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Version</label>
                        <input
                          type="text"
                          value={editingPrompt.prompt_version || ''}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_version: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="1.0.0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={editingPrompt.prompt_type || ''}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Select...</option>
                          {PROMPT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          {/* Preserve a legacy/unknown stored type so re-saving
                              doesn't silently rewrite it to blank. */}
                          {editingPrompt.prompt_type
                            && !(PROMPT_TYPES as readonly string[]).includes(editingPrompt.prompt_type) && (
                            <option value={editingPrompt.prompt_type}>
                              {editingPrompt.prompt_type} (legacy)
                            </option>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                        <select
                          value={editingPrompt.prompt_state || ''}
                          onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          {PROMPT_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Traffic %</label>
                        <div className="relative">
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={editingPrompt.traffic_pct != null && editingPrompt.traffic_pct !== ''
                              ? String(Math.round(parseFloat(String(editingPrompt.traffic_pct)) * 100 * 10) / 10)
                              : ''}
                            onChange={(e) => {
                              const pct = parseFloat(e.target.value);
                              if (isNaN(pct)) { setEditingPrompt({ ...editingPrompt, traffic_pct: '' }); return; }
                              const clamped = Math.min(100, Math.max(0, pct));
                              setEditingPrompt({ ...editingPrompt, traffic_pct: String(Math.round(clamped / 100 * 10000) / 10000) });
                            }}
                            className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0.0"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">%</span>
                        </div>
                        <p className="mt-1 text-[10px] text-gray-500 leading-snug">
                          {parseFloat(String(editingPrompt.traffic_pct)) > 1
                            ? 'legacy value stored on the 0–100 scale — run scripts/normalize_traffic_pct.py'
                            : `stored as a 0–1 fraction: ${formatTrafficPct(editingPrompt.traffic_pct)}`}
                        </p>
                      </div>
                    </div>

                    {/* Row 2: Model, Max Tokens, Temperature */}
                    {(() => {
                      const assignedModel = models.find(m => m.model_id === lightboxModelId);
                      const isEmbeddingModel = assignedModel?.model_type === 'embedding';
                      return (
                    <div className="grid grid-cols-3 gap-3">
                      {lightboxFunctionId ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
                          <select
                            value={lightboxModelId}
                            onChange={(e) => setLightboxModelId(e.target.value)}
                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="">Select model...</option>
                            {enabledModels.map((m: any) => (
                              <option key={m.model_id} value={m.model_id}>
                                {m.display_name || m.model_id}{m.model_type === 'embedding' ? ' [Embedding]' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : <div />}
                      {lightboxFunctionId ? (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Max Tokens</label>
                          <input
                            type="number"
                            min="0"
                            step="256"
                            value={isEmbeddingModel ? '' : lightboxMaxTokens}
                            onChange={(e) => setLightboxMaxTokens(e.target.value)}
                            disabled={isEmbeddingModel}
                            className={`w-full px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                              isEmbeddingModel ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300'
                            }`}
                            placeholder={isEmbeddingModel ? 'N/A' : '8192'}
                            title={isEmbeddingModel ? 'Max tokens is not applicable for embedding models' : ''}
                          />
                        </div>
                      ) : <div />}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Temperature</label>
                        {editingPrompt.prompt_type === 'snippet' || isEmbeddingModel ? (
                          <input
                            type="text"
                            value="N/A"
                            disabled
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono bg-gray-100 text-gray-400 cursor-not-allowed"
                            title={isEmbeddingModel ? 'Temperature is not applicable for embedding models' : 'Temperature is not applicable for snippets'}
                          />
                        ) : (
                          <input
                            type="number"
                            step="0.05"
                            min="0"
                            max="1"
                            value={editingPrompt.temperature != null && editingPrompt.temperature !== '' ? String(editingPrompt.temperature) : ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '') { setEditingPrompt({ ...editingPrompt, temperature: '' }); return; }
                              const num = parseFloat(val);
                              if (!isNaN(num)) {
                                setEditingPrompt({ ...editingPrompt, temperature: String(Math.min(1, Math.max(0, num))) });
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            placeholder="0.7"
                          />
                        )}
                        {!(editingPrompt.prompt_type === 'snippet' || isEmbeddingModel) && (
                          <p className="mt-1 text-[10px] text-gray-500 leading-snug">
                            {temperaturePolicyHint(editingPrompt.prompt_type)}
                          </p>
                        )}
                      </div>
                    </div>
                      );
                    })()}

                    {/* Function Description (only for functions, not snippets) */}
                    {lightboxFunctionId && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Function Description</label>
                        <textarea
                          value={lightboxDescription}
                          onChange={(e) => setLightboxDescription(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                          placeholder="Describe what this function does..."
                        />
                      </div>
                    )}

                    {/* Post-Processors (only for functions, not snippets) */}
                    {lightboxFunctionId && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                            <Settings2 className="w-3.5 h-3.5" />
                            Post-Processors
                            <span className="text-[10px] text-gray-400 font-normal ml-1">
                              ({lightboxPostProcessors.length} configured)
                            </span>
                          </label>
                          {postProcessorRegistry.length > 0 && (
                            <select
                              value=""
                              onChange={(e) => {
                                const processorName = e.target.value;
                                if (!processorName) return;
                                const meta = postProcessorRegistry.find(p => p.name === processorName);
                                if (!meta) return;
                                // Build default params from registry metadata
                                const defaultParams: Record<string, unknown> = {};
                                Object.entries(meta.params).forEach(([key, param]) => {
                                  if (param.default !== undefined) {
                                    defaultParams[key] = param.default;
                                  } else if (param.type === 'int') {
                                    defaultParams[key] = 0;
                                  } else if (param.type === 'bool') {
                                    defaultParams[key] = false;
                                  } else {
                                    defaultParams[key] = '';
                                  }
                                });
                                setLightboxPostProcessors(prev => [...prev, { fn: processorName, params: defaultParams }]);
                              }}
                              className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value="">+ Add processor...</option>
                              {postProcessorRegistry.map(p => (
                                <option key={p.name} value={p.name}>{p.name}</option>
                              ))}
                            </select>
                          )}
                          {postProcessorRegistry.length === 0 && (
                            <span className="text-[10px] text-gray-400 italic">Registry unavailable</span>
                          )}
                        </div>
                        {lightboxPostProcessors.length > 0 ? (
                          <div className="space-y-2">
                            {lightboxPostProcessors.map((proc, idx) => {
                              const meta = postProcessorRegistry.find(p => p.name === proc.fn);
                              return (
                                <div key={idx} className="border border-gray-200 rounded-lg p-2.5 bg-gray-50">
                                  <div className="flex items-center justify-between mb-1.5">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-gray-400 font-mono w-4 text-center">{idx + 1}</span>
                                      <span className="text-xs font-medium font-mono text-violet-700">{proc.fn}</span>
                                      {meta && (
                                        <span className="text-[10px] text-gray-400" title={meta.description}>
                                          {meta.description.length > 50 ? meta.description.substring(0, 50) + '...' : meta.description}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => {
                                          if (idx === 0) return;
                                          setLightboxPostProcessors(prev => {
                                            const next = [...prev];
                                            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                                            return next;
                                          });
                                        }}
                                        disabled={idx === 0}
                                        className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move up"
                                      >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (idx === lightboxPostProcessors.length - 1) return;
                                          setLightboxPostProcessors(prev => {
                                            const next = [...prev];
                                            [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                                            return next;
                                          });
                                        }}
                                        disabled={idx === lightboxPostProcessors.length - 1}
                                        className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move down"
                                      >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setLightboxPostProcessors(prev => prev.filter((_, i) => i !== idx));
                                        }}
                                        className="p-0.5 text-red-400 hover:text-red-600 ml-1"
                                        title="Remove processor"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  {/* Parameter fields */}
                                  {meta && Object.keys(meta.params).length > 0 && (
                                    <div className="grid grid-cols-2 gap-2 mt-1.5 pl-6">
                                      {Object.entries(meta.params).map(([paramName, paramMeta]) => (
                                        <div key={paramName}>
                                          <label className="block text-[10px] text-gray-500 mb-0.5" title={paramMeta.description}>
                                            {paramName}
                                            {paramMeta.required && <span className="text-red-400 ml-0.5">*</span>}
                                          </label>
                                          {paramMeta.type === 'bool' ? (
                                            <label className="flex items-center gap-1.5 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={Boolean(proc.params[paramName])}
                                                onChange={(e) => {
                                                  setLightboxPostProcessors(prev => prev.map((p, i) =>
                                                    i === idx ? { ...p, params: { ...p.params, [paramName]: e.target.checked } } : p
                                                  ));
                                                }}
                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                              />
                                              <span className="text-[10px] text-gray-500">{paramMeta.description}</span>
                                            </label>
                                          ) : paramMeta.type === 'int' ? (
                                            <input
                                              type="number"
                                              value={proc.params[paramName] !== undefined ? String(proc.params[paramName]) : ''}
                                              onChange={(e) => {
                                                const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                                                setLightboxPostProcessors(prev => prev.map((p, i) =>
                                                  i === idx ? { ...p, params: { ...p.params, [paramName]: val } } : p
                                                ));
                                              }}
                                              placeholder={paramMeta.default !== undefined ? String(paramMeta.default) : paramMeta.description}
                                              title={paramMeta.description}
                                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                            />
                                          ) : (
                                            <input
                                              type="text"
                                              value={proc.params[paramName] !== undefined ? String(proc.params[paramName]) : ''}
                                              onChange={(e) => {
                                                setLightboxPostProcessors(prev => prev.map((p, i) =>
                                                  i === idx ? { ...p, params: { ...p.params, [paramName]: e.target.value } } : p
                                                ));
                                              }}
                                              placeholder={paramMeta.default !== undefined ? String(paramMeta.default) : paramMeta.description}
                                              title={paramMeta.description}
                                              className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                            />
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {/* Show raw params if no registry metadata (processor deployed but not in registry) */}
                                  {!meta && proc.params && Object.keys(proc.params).length > 0 && (
                                    <div className="pl-6 mt-1">
                                      <span className="text-[10px] text-amber-500 italic">Unknown processor - params shown as JSON</span>
                                      <pre className="text-[10px] font-mono text-gray-500 mt-0.5">{JSON.stringify(proc.params, null, 2)}</pre>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic py-2 text-center border border-dashed border-gray-200 rounded-lg">
                            No post-processors configured
                          </div>
                        )}
                      </div>
                    )}

                    {/* Prompt Text + Last Updated */}
                    <div>
                      <div className="flex items-baseline justify-between mb-1">
                        <label className="text-xs font-medium text-gray-600">Prompt Text</label>
                        {editingPrompt.updated_at && (
                          <span className="text-[10px] text-gray-400">
                            Updated: {new Date(editingPrompt.updated_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {(() => {
                        const lbModel = models.find(m => m.model_id === lightboxModelId);
                        if (lbModel?.model_type === 'embedding') {
                          return (
                            <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Info className="w-4 h-4 text-cyan-600" />
                                <span className="text-sm font-medium text-cyan-800">Embedding Model</span>
                              </div>
                              <p className="text-sm text-cyan-700">
                                Embedding models accept tag text for vector matching -- no LLM prompt is needed.
                                The resume and job tags are sent directly to the model for similarity scoring.
                              </p>
                            </div>
                          );
                        }
                        const staticHasParams = /\{\{[A-Z_][A-Z0-9_]*\}\}/.test(editingPrompt.prompt_text || '');
                        const variableProvided = !!(editingPrompt.prompt_text_variable || '').trim();
                        return (
                          <div className="space-y-2">
                            <div>
                              <div className="flex items-baseline justify-between mb-0.5">
                                <span className="text-[11px] text-gray-500 uppercase tracking-wide">Static (cacheable)</span>
                                <span className="text-[10px] text-gray-400 italic">
                                  Sent with cache_control: ephemeral. Snippets OK; runtime parameters break the cache key.
                                </span>
                              </div>
                              <textarea
                                value={editingPrompt.prompt_text || ''}
                                onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_text: e.target.value })}
                                rows={16}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                                placeholder="Stable instructions, schema, examples — anything that does not change per call."
                              />
                              {staticHasParams && variableProvided && (
                                <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                  <span>
                                    Static contains <code className="font-mono">{'{{PLACEHOLDER}}'}</code> tokens.
                                    If any are runtime parameters (not snippets), they will invalidate the prompt cache
                                    on every call. Move per-call parameters to the Variable section below.
                                  </span>
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="flex items-baseline justify-between mb-0.5">
                                <span className="text-[11px] text-gray-500 uppercase tracking-wide">Variable (non-cached)</span>
                                <span className="text-[10px] text-gray-400 italic">
                                  Optional. Per-call inputs go here. Leave blank for legacy single-block prompts.
                                </span>
                              </div>
                              <textarea
                                value={editingPrompt.prompt_text_variable || ''}
                                onChange={(e) => setEditingPrompt({ ...editingPrompt, prompt_text_variable: e.target.value })}
                                rows={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-y"
                                placeholder="# INPUT&#10;{{RESUME_DATA}}"
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Save Controls */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={autoIncrementPatch}
                          onChange={(e) => {
                            setAutoIncrementPatch(e.target.checked);
                            localStorage.setItem('admin_auto_increment_patch', String(e.target.checked));
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        Increment patch +1 during save
                      </label>
                      <div className="flex items-center gap-3">
                        {!selectedVersion && (
                          <span className="text-xs text-amber-600 font-medium">Unsaved new version</span>
                        )}
                        <button
                          onClick={savePrompt}
                          disabled={promptSaving}
                          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors text-sm font-medium"
                        >
                          {promptSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                    {promptLoading ? 'Loading...' : 'Select a version from the left panel'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
